/* eslint-disable no-console */
import { isStandalone, isSodimacRetail } from '@digital-retail/store-manager';
import PropTypes from 'prop-types';
import { useEffect, useRef } from 'react';
import apiConfig from '../config/api/apiConfig';
import { constants } from '../config/constants';
import { storeMap } from '../config/storeMap';
import SearchLandingContainer from '../containers/SearchLanding/SearchLandingContainer';
import { getHostUrl } from '../containers/SearchResults/utils';
import { withApplicationContext } from '../utils/ApplicationContext';
import { CartContextProvider } from '../utils/CartContext';
import { CookieStorage } from '../utils/CookieStorage';
import { navigate, redirect } from '../utils/RouteHandler';
import { SLPSeo } from '../utils/SEO/SLPSeo';
import { getHeadersForSponsors } from '../utils/SponsoredHelper';
import { WebStorage } from '../utils/WebStorage';
import { WithUrl } from '../utils/WithUrl';
import { addProductListEvent } from '../utils/dataLayerHelper';
import { isWindow } from '../utils/deviceChecker';
import { useOnScroll } from '../utils/hooks/UseOnScroll';
import { httpService } from '../utils/httpService';
import { isKiosk } from '../utils/kisokHelper';
import { handleScroll } from '../utils/paginationUtils';
import { getPaginatedUrl as getPaginatedUrlUtil, getRequestUrl } from '../utils/urlHelper';
import Error from './_error';

const updateLocation = () => {
  WebStorage.setItem({
    name: constants.SESSION_STORAGE_SCROLL,
    value: `${window.scrollX},${window.scrollY}`,
  });
};

const Search = (props) => {
  const { statusCode, isImageSearch } = props;
  // eslint-disable-next-line react/prop-types
  const { appCtx, ...initialData } = props; // InitialData should be empty
  const { regionCode, host } = appCtx;

  const data = useRef();
  const isInitialDataEmpty = Object.entries(initialData).length === 0;
  if (!isInitialDataEmpty) {
    data.current = initialData;
  }

  const {
    searchTerm,
    pagination,
    facets,
    baseFacetsURL,
    appliedFacets,
    autoCorrections,
    availabilityFacets,
    results,
    sortOptions,
    currentLocationId,
    site,
    layout,
    keywordRelatedBannerHtml,
    leftPromotionContent,
    relatedKeywords,
    currentUrl,
    promotedFilter,
    store,
    metadata,
    isShopPage,
    shopPageId,
    pageName,
  } = data.current || {};

  const storeValidated = store && storeMap[store.toLowerCase()] ? store : null;

  useEffect(() => {
    appCtx.attachResizeListener();
  }, [data.current]);

  useEffect(() => {
    handleScroll(appCtx);
    if (results) {
      addProductListEvent(results, 'search', storeValidated, regionCode);
    }
  }, [results]);

  useOnScroll({
    handler: updateLocation,
  });

  const getPaginatedUrl = (paginationIndex) => {
    return getPaginatedUrlUtil(currentUrl, paginationIndex, store, regionCode);
  };

  if (statusCode === 500) {
    return <Error isImageSearch={isImageSearch} />;
  }

  return !isInitialDataEmpty ? (
    <>
      <SLPSeo
        SiteProps={{
          regionCode,
          searchTerm: decodeURIComponent(searchTerm).trim().replace(/ /g, '+'),
          host,
          store: storeValidated,
          facets,
          metadata,
          isShopPage,
          shopPageId,
        }}
      />
      <CartContextProvider>
        <SearchLandingContainer
          pagination={{
            data: pagination,
            getPaginatedUrl,
          }}
          layout={layout}
          products={results}
          promotedFilter={promotedFilter}
          autoCorrections={autoCorrections}
          facets={facets}
          baseFacetsURL={baseFacetsURL}
          appliedFacets={appliedFacets}
          searchTerm={searchTerm}
          availabilityFacets={availabilityFacets}
          sortOptions={sortOptions}
          keywordRelatedBannerHtml={keywordRelatedBannerHtml}
          leftPromotionContent={leftPromotionContent}
          site={site}
          currentLocationId={currentLocationId}
          relatedKeywords={relatedKeywords}
          metadata={metadata}
          isShopPage={isShopPage}
          pageName={pageName}
        />
      </CartContextProvider>
    </>
  ) : null;
};

const shouldRedirect = (responseType) => {
  return responseType === constants.SEARCH_REDIRECT_STATUS;
};

const redirectSearch = (response, url, redirectCode) => {
  if (isWindow()) {
    redirect(url, true);
    return;
  }

  if (response) {
    response.writeHead(redirectCode, { Location: url });
    response.end();
  }
};

const redirectNoSearchResult = (response, site, regionCode, searchTerm, store, extraParams = {}) => {
  const termo = searchTerm.trim().replace(/%20/g, '+');
  const queryParams = { Ntt: termo, ...extraParams };
  const queryString = new URLSearchParams(queryParams).toString();
  if (isWindow()) {
    navigate({
      url: '/noResult',
      params: queryParams,
      site,
    });
    return;
  }

  if (response) {
    const url = `/${storeMap[store] || 'falabella'}-${regionCode}/noResult?${queryString}`;
    response.writeHead(constants.HTTP_RES_CODE.TEMP_REDIRECT, { Location: url });
    response.end();
  }
};

const redirectToMarketplace = (response, url, redirectCode = constants.HTTP_RES_CODE.TEMP_REDIRECT) => {
  if (isWindow()) {
    redirect(url, true);
    return;
  }

  if (response) {
    response.writeHead(redirectCode, { Location: url });
    response.end();
  }
};
Search.getInitialProps = async ({ config }, { query, res, req, asPath }, appCtx) => {
  const { site, id, isImageSearch, ...rest } = query;
  const { deviceType, tenant, regionCode, latLong } = appCtx;
  const store = appCtx?.store ?? null;
  const siteId = `${query.site.substr(query.site.indexOf('-') + 1, 2)}`;
  const urlPath = asPath?.match('(/{0,1}[^?#]*)')[0].split('/');
  const getPageName = () => {
    const pagePath = urlPath?.[urlPath.length - 2];

    if (id && (pagePath === 'shop' || pagePath === 'b')) {
      return 'shop';
    }
    return 'search';
  };
  const pageName = getPageName();
  const isShopPage = pageName === 'shop';
  const searchTerm =
    (query.Ntt && encodeURIComponent(query.Ntt)) ||
    encodeURIComponent(decodeURIComponent(id)?.trim().replace(/-/g, ' '));

  rest.pgid = appCtx.priceGroupId || rest.pgid;
  rest.pid = appCtx.politicalId || rest.pid;
  rest.zones = appCtx.zones || rest.zones;
  if (latLong) {
    rest.latLong = encodeURIComponent(JSON.stringify(latLong));
  }

  const params = {};
  Object.keys(rest).forEach((key) => {
    const value = rest[key];
    if (value) {
      params[encodeURIComponent(key)] = encodeURIComponent(value);
    }
  });

  if (store) {
    params[encodeURIComponent('store')] = encodeURIComponent(store);
  }

  const isDebugAdsEnabled = isWindow() ? CookieStorage.getCookie('isDebugAdsEnabled') === 'true' : false;

  if (isDebugAdsEnabled) {
    params.debug = true;
  }
  if (isKiosk(appCtx)) {
    params.channel = 'KIOSK';
  }

  const cookies = CookieStorage.parseCookies(appCtx.ssrCookies);

  if (appCtx.isTottusSA) {
    if (cookies.USER_ID) {
      params.userId = cookies.USER_ID;
    }

    params.standardZones = appCtx.standardZones;
    params.expressZones = appCtx.expressZones;
  }

  if (id) {
    params.pageName = pageName;
    params.pageId = encodeURIComponent(id);
    if (!query.Ntt) {
      params.Ntt = searchTerm;
    }
  }
  const getUrl = isShopPage ? apiConfig.getShopUrl : apiConfig.getProductsUrl;
  const getSearchUrl = getUrl({
    base: getHostUrl({ config, store, regionCode }),
    siteId,
    params,
  });
  const currentLocationId = appCtx.politicalId;
  const customHeaders = {};
  if (isImageSearch) {
    customHeaders.isImageSearch = true;
  }

  const headersForProductSearch = isWindow() ? {} : { cookie: appCtx.ssrCookies };
  const headersForSponsors = getHeadersForSponsors(deviceType, tenant);
  const response = await httpService(appCtx).get(
    getSearchUrl,
    {
      headers: {
        'Content-Type': 'application/json',
        'x-device-type': appCtx.deviceType,
        'x-channel-id': appCtx.channel,
        ...headersForProductSearch,
        ...headersForSponsors,
        ...customHeaders,
      },
    },
    'json'
  );

  const isImageSearchResponseHeader = response.headers?.get('isImageSearch');

  const { data, error, statusCode } = response;
  if (error && statusCode !== 409 && !shouldRedirect(data.responseType)) {
    return { statusCode: 500, isImageSearch: isImageSearchResponseHeader === 'true' || isImageSearchResponseHeader };
  }
  if (shouldRedirect(data.responseType)) {
    if (!isWindow() && res && !(res.finished || res.headersSent)) {
      res.setHeader('Cache-Control', 'no-cache');
    }
    return redirectSearch(res, data.data.altUrl, constants.HTTP_RES_CODE.PERM_REDIRECT);
  }
  const metadata = data?.data?.metadata ?? {};
  const {
    data: {
      data: {
        results,
        layout,
        facets,
        appliedFacets,
        baseFacetsURL,
        availabilityFacets,
        sortOptions,
        pagination,
        autoCorrections,
        relatedKeywordBanner,
        leftPromotionContent,
        relatedKeywords,
        promotedFilter,
      },
    },
  } = response;

  if (results && results.length === 0) {
    if (!isWindow() && res && !(res.finished || res.headersSent)) {
      res.setHeader('Cache-Control', 'no-cache');
    }
    const isSearchInMarketplaceEnabled = appCtx?.siteConfig?.toggles?.isSearchInMarketplaceEnabled ?? true;
    if (store && !isStandalone({ store }) && isSearchInMarketplaceEnabled && !isSodimacRetail(store)) {
      const marketplaceUrl = WithUrl(isStandalone({ store }) ? 'SA_SEARCH_PAGE' : 'SEARCH_PAGE', regionCode, {
        custom: {
          searchTerm,
        },
      });
      redirectToMarketplace(res, marketplaceUrl);
    } else if (isImageSearchResponseHeader === 'true') {
      redirectNoSearchResult(res, site, regionCode, searchTerm || '', store, { isImageSearch: true });
    } else {
      redirectNoSearchResult(res, site, regionCode, searchTerm || '', store);
    }
    return undefined;
  }

  let keywordRelatedBannerHtml = '';
  if (relatedKeywordBanner) {
    if (config.FETCH_BANNERS_IN_SERVER_SIDE === false && relatedKeywordBanner.includes('assets.contentstack')) {
      keywordRelatedBannerHtml = relatedKeywordBanner;
    } else {
      const { data: htmlData, error: keywordBannerError } = await httpService().get(relatedKeywordBanner, {
        includeTracing: false,
        asText: true,
      });

      if (!keywordBannerError) {
        keywordRelatedBannerHtml = htmlData;
      }
    }
  }

  const currentUrl = isWindow() ? asPath : getRequestUrl(req.url, appCtx.tenant);

  return {
    results,
    layout,
    facets,
    baseFacetsURL,
    appliedFacets,
    searchTerm,
    pagination,
    sortOptions,
    autoCorrections,
    availabilityFacets,
    currentLocationId,
    site: encodeURIComponent(query.site),
    relatedKeywords,
    keywordRelatedBannerHtml,
    leftPromotionContent,
    currentUrl,
    promotedFilter,
    store: query.store,
    metadata,
    isShopPage,
    shopPageId: id,
    pageName,
  };
};

Search.propTypes = {
  searchTerm: PropTypes.string.isRequired,
  results: PropTypes.arrayOf(PropTypes.object).isRequired,
  facets: PropTypes.arrayOf(PropTypes.object).isRequired,
  appliedFacets: PropTypes.array.isRequired,
  pagination: PropTypes.object.isRequired,
  site: PropTypes.string.isRequired,
  appCtx: PropTypes.object.isRequired,
  sortOptions: PropTypes.array.isRequired,
  autoCorrections: PropTypes.array.isRequired,
  availabilityFacets: PropTypes.array.isRequired,
  baseFacetsURL: PropTypes.string.isRequired,
  currentLocationId: PropTypes.string.isRequired,
  keywordRelatedBannerHtml: PropTypes.string.isRequired,
  statusCode: PropTypes.string.isRequired,
  isImageSearch: PropTypes.bool.isRequired,
};

export { redirectNoSearchResult, redirectToMarketplace, Search };

export default withApplicationContext(Search);
